import { useAppSSE } from 'contexts/AppSSEContext';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import apiClient from 'services/_infra/apiClient';

/**
 * Shared responder personnel state and live event data.
 */
export const PersonnelContext = createContext();

/**
 * Provides personnel roster data, geolocation history, and live event subscriptions.
 * @param {{children: React.ReactNode, shouldFetchEventHistory?: boolean}} props
 * @returns {JSX.Element}
 */
export const PersonnelProvider = ({ children, shouldFetchEventHistory = true }) => {
  const [personnel, setPersonnel] = useState({});
  const [geolocationHistory, setGeolocationHistory] = useState({});
  const [lastUpdateTime, setLastUpdateTime] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [isPersonnelLoading, setIsPersonnelLoading] = useState(true);
  const [hasLoadedPersonnel, setHasLoadedPersonnel] = useState(false);
  const { subscribe } = useAppSSE();

  const fetchEvents = useCallback(async () => {
    try {
      const res = await apiClient.get(`/responder/event-history`);
      if (res?.data?.results) {
        setAllEvents(res.data.results);
      }
    } catch (err) {
      console.error('Failed to fetch events', err);
    }
  }, []);

  useEffect(() => {
    if (!shouldFetchEventHistory) return;
    fetchEvents();
  }, [fetchEvents, shouldFetchEventHistory]);

  /**
   * Normalizes personnel update payloads from SSE and merges by `userId`.
   * @param {any} personnelUpdate
   */
  const updatePersonnelInMemory = useCallback((personnelUpdate) => {
    const normalizedUpdates = (() => {
      if (!personnelUpdate) return [];
      if (Array.isArray(personnelUpdate)) return personnelUpdate;
      if (personnelUpdate?.personnel) {
        return Array.isArray(personnelUpdate.personnel)
          ? personnelUpdate.personnel
          : Object.values(personnelUpdate.personnel);
      }
      if (personnelUpdate?.userId != null) return [personnelUpdate];
      if (typeof personnelUpdate === 'object') return Object.values(personnelUpdate);
      return [];
    })();

    if (normalizedUpdates.length === 0) return;

    setPersonnel((prevPersonnel) => {
      const updated = { ...prevPersonnel };
      normalizedUpdates.forEach((person) => {
        if (person?.userId == null) return;

        // Merge the update into the existing person object, if present
        updated[person.userId] = {
          ...(updated[person.userId] || {}),
          ...person,
        };
      });

      return updated;
    });
  }, []);

  /**
   * Appends a geolocation event to in-memory history by user id.
   * @param {{userId?: string|number}} geolocationUpdate
   */
  const updateGeolocationHistoryInMemory = useCallback((geolocationUpdate) => {
    if (!geolocationUpdate?.userId) return;
    setGeolocationHistory((prevHistory) => {
      const updated = { ...prevHistory };
      const nextEntries = updated[geolocationUpdate.userId]
        ? [...updated[geolocationUpdate.userId], geolocationUpdate]
        : [geolocationUpdate];
      updated[geolocationUpdate.userId] = nextEntries;
      return updated;
    });
  }, []);

  /**
   * Normalizes event payloads into a flat array shape for the history feed.
   * @param {any|any[]} payload
   * @param {string} fallbackEvent
   * @returns {any[]}
   */
  const normalizeEventEntries = useCallback((payload, fallbackEvent) => {
    const items = Array.isArray(payload) ? payload : [payload];
    return items
      .map((item) => {
        if (!item || typeof item !== 'object') return null;
        return {
          ...item,
          event: item.event || item.type || fallbackEvent,
        };
      })
      .filter(Boolean);
  }, []);

  /**
   * Fetches responder personnel and stores it as a `userId` keyed map.
   * @returns {Promise<void>}
   */
  const fetchPersonnel = useCallback(async () => {
    setIsPersonnelLoading(true);
    try {
      const res = await apiClient.get(`/responder/personnel`);
      if (res?.data?.personnel == null) return;

      const personnelArray = Array.isArray(res.data.personnel)
        ? res.data.personnel
        : Object.values(res.data.personnel);
      const personnelById = {};

      personnelArray.forEach((person) => {
        if (person?.userId != null) {
          personnelById[person.userId] = person;
        }
      });

      setPersonnel(personnelById);
      setLastUpdateTime(new Date());
    } catch (err) {
      console.error('Failed to fetch personnel', err);
    } finally {
      setIsPersonnelLoading(false);
      setHasLoadedPersonnel(true);
    }
  }, []);

  useEffect(() => {
    fetchPersonnel();

    const unsubPersonnel = subscribe('personnel-update', (data) => {
      try {
        updatePersonnelInMemory(data);
        setLastUpdateTime(new Date());
      } catch (err) {
        console.error('Failed to handle personnel SSE payload', err);
      }
    });

    const unsubGeo = subscribe('geolocation-trigger', (data) => {
      try {
        updateGeolocationHistoryInMemory(data.data);
        setLastUpdateTime(new Date());
        const normalized = normalizeEventEntries(data.data, 'geolocation-trigger');
        if (normalized.length) {
          setAllEvents((prev) => [...prev, ...normalized]);
        }
      } catch (err) {
        console.error('Failed to handle geolocation SSE payload', err);
      }
    });

    return () => {
      unsubPersonnel();
      unsubGeo();
    };
  }, [
    fetchPersonnel,
    subscribe,
    normalizeEventEntries,
    updateGeolocationHistoryInMemory,
    updatePersonnelInMemory,
  ]);

  const contextValue = {
    personnel,
    setPersonnel,
    geolocationHistory,
    setGeolocationHistory,
    fetchPersonnel,
    isPersonnelLoading,
    hasLoadedPersonnel,
    lastUpdateTime,
    allEvents,
  };

  return <PersonnelContext.Provider value={contextValue}>{children}</PersonnelContext.Provider>;
};

/**
 * Hook for consuming personnel context.
 * @returns {any}
 */
export const usePersonnel = () => {
  const context = useContext(PersonnelContext);
  if (!context) {
    throw new Error('usePersonnel must be used within a PersonnelProvider');
  }
  return context;
};
